import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import {
  SpendQueryType,
  useCompanySpend,
} from "../../../hooks/useCompanySpend";
import { useCompanyPageStore } from "../../../../../store/companyPageStore";
import Skeleton from "@mui/material/Skeleton";
import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import Stack from "@mui/material/Stack";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { getNumberLabel } from "../../../../../utils/numberUtils";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import {
  transformFlatToHierarchicalSpend,
  createProportionalChart,
  HierarchicalSpendItem,
  convertToProportionalData,
} from "./utils";
import { useHierarchyRelations } from "../../../../../hooks/useHierarchyRelations";
import { CompanySpend } from "../../../../../legacy/queries/Companies";

am4core.useTheme(am4themes_animated);

interface BreadcrumbNavigationProps {
  currentPath: string[];
  onBreadcrumbClick: (index: number) => void;
}

interface ChartContentProps {
  hierarchicalData: HierarchicalSpendItem[];
  currentPath: string[];
  currentPathData: HierarchicalSpendItem[];
  onBreadcrumbClick: (index: number) => void;
  onDrillDown: (categoryName: string) => void;
}

// Breadcrumb component
const BreadcrumbNavigation = ({
  currentPath,
  onBreadcrumbClick,
}: BreadcrumbNavigationProps) => {
  // Filter out "Total IT" from the path to avoid duplication
  const filteredPath = currentPath.filter((segment) => segment !== "Total IT");

  return (
    <Stack direction="row" alignItems="center" mb={1} ml={1} height={30}>
      {currentPath.length > 0 && (
        <IconButton
          size="small"
          onClick={() => onBreadcrumbClick(-1)}
          disableRipple
          sx={{ p: 0, mr: 1 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      )}
      <Breadcrumbs separator="/" sx={{ fontSize: "0.75rem" }}>
        <Link
          component="button"
          variant="caption"
          onClick={() => onBreadcrumbClick(-1)}
          sx={{ textDecoration: "none" }}
        >
          {filteredPath.length === 0 ? "Total IT /" : "Total IT"}
        </Link>
        {filteredPath.map((segment, index) => {
          // Calculate the original index in currentPath for proper navigation
          const originalIndex = currentPath.indexOf(segment);
          return (
            <Link
              key={index}
              component="button"
              variant="caption"
              onClick={() => onBreadcrumbClick(originalIndex)}
              sx={{
                textDecoration: "none",
                fontWeight:
                  index === filteredPath.length - 1 ? "bold" : "normal",
              }}
            >
              {segment}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Stack>
  );
};

// Chart content component
const ChartContent = ({
  hierarchicalData,
  currentPath,
  currentPathData,
  onBreadcrumbClick,
  onDrillDown,
}: ChartContentProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<am4charts.XYChart | null>(null);

  const total = hierarchicalData.reduce((sum, item) => sum + item.value, 0);

  // Handle drill down
  const handleDrillDown = useCallback(
    (categoryName: string) => {
      const category = currentPathData.find(
        (item) => item.name === categoryName
      );
      if (category?.children?.length) {
        onDrillDown(categoryName);
      }
    },
    [currentPathData, onDrillDown]
  );

  // Create chart using utils
  useEffect(() => {
    if (!chartRef.current || !currentPathData || currentPathData.length === 0) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
      return;
    }

    // Convert hierarchical data to proportional data with percentages
    const proportionalData = convertToProportionalData(currentPathData);

    // Create chart using utils function
    const chart = createProportionalChart(
      chartRef.current,
      proportionalData,
      handleDrillDown
    );
    chartInstanceRef.current = chart;

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [currentPathData, handleDrillDown]);

  return (
    <Box>
      {/* Breadcrumb navigation */}
      <BreadcrumbNavigation
        currentPath={currentPath}
        onBreadcrumbClick={onBreadcrumbClick}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, ml: 1, display: "block" }}
      >
        {currentPath.length === 0 ? (
          <>
            <strong>Total IT spend:</strong>{" "}
            {getNumberLabel(total, { isCurrency: true, compact: true })}
          </>
        ) : (
          <>
            <strong>Current Level Spend:</strong>{" "}
            {getNumberLabel(
              currentPathData.reduce((sum, item) => sum + item.value, 0),
              { isCurrency: true, compact: true }
            )}
          </>
        )}
      </Typography>
      <div ref={chartRef} style={{ width: "100%", height: 80 }} />
    </Box>
  );
};

const DEFAULT_PATH = ["Total IT", "Total External IT"];

export const ProportionalITSpend = () => {
  const company = useCompanyPageStore((state) => state.company);
  const [currentPath, setCurrentPath] = useState<string[]>(DEFAULT_PATH);

  const { data, loading, error } = useCompanySpend({
    companyId: company?.hgId,
    queryType: SpendQueryType.BY_CATEGORY,
    per: 1000,
    skip: !company?.hgId,
  });

  // Transform flat data to hierarchical structure
  const hierarchicalData = useMemo(() => {
    if (!data?.data) return [];
    return transformFlatToHierarchicalSpend(data.data as CompanySpend[]);
  }, [data]);

  // Use the hierarchy relations hook
  const { getDataAtPath } = useHierarchyRelations(hierarchicalData);

  // Get current level data using the hook function with 'name' field
  const currentPathData = useMemo(
    () => getDataAtPath(currentPath, "name"),
    [getDataAtPath, currentPath]
  );

  // Handle drill down - moved to parent level to manage state
  const handleDrillDown = useCallback(
    (categoryName: string) => {
      setCurrentPath([...currentPath, categoryName]);
    },
    [currentPath]
  );

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = useCallback(
    (index: number) => {
      if (index === -1) {
        // Back button: go to parent (remove last item) instead of root
        if (currentPath.length > 0) {
          setCurrentPath(currentPath.slice(0, -1));
        }
      } else {
        setCurrentPath(currentPath.slice(0, index + 1));
      }
    },
    [currentPath]
  );

  const cardContent = useMemo(() => {
    if (loading || !company) {
      return <Skeleton variant="rectangular" width="100%" height={60} />;
    }
    if (error) {
      return (
        <Stack
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={60}
        >
          <Typography color="error" variant="body2">
            Error loading data.
          </Typography>
        </Stack>
      );
    }

    if (
      data?.count &&
      hierarchicalData.length > 0 &&
      currentPathData.length > 0
    ) {
      return (
        <ChartContent
          hierarchicalData={hierarchicalData}
          currentPath={currentPath}
          currentPathData={currentPathData}
          onBreadcrumbClick={handleBreadcrumbClick}
          onDrillDown={handleDrillDown}
        />
      );
    }

    return (
      <Stack
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={60}
      >
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Stack>
    );
  }, [
    loading,
    error,
    data,
    company,
    hierarchicalData,
    currentPathData,
    currentPath,
    handleBreadcrumbClick,
    handleDrillDown,
  ]);

  return (
    <Card sx={{ p: 3, height: 180 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
        Proportional IT Spend
      </Typography>
      {cardContent}
    </Card>
  );
};
